import React, { useEffect, useState, useCallback } from "react";
import { Download, X, Share, Plus, Smartphone } from "lucide-react";
import { useT } from "@/i18n/LanguageContext";

/**
 * PWAInstall — smart, cross-platform "Add to Home Screen" prompt.
 *
 * Behaviour:
 * - Chrome/Edge (Android + Desktop): captures the `beforeinstallprompt` event
 *   and shows a native install banner with a single-tap Install button.
 * - iOS Safari: iOS doesn't fire the event. We detect iOS + Safari and show
 *   a custom banner with Share → "Add to Home Screen" instructions.
 * - Hidden entirely when: already installed (display-mode: standalone), user
 *   dismissed within the last 7 days, or launched from a PWA shortcut.
 * - Delayed first appearance by 8s so users get context before we ask.
 */

const DISMISS_KEY = "miresco_pwa_dismissed_at";
const DISMISS_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  const iOSDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  // Also detect iPadOS 13+ which reports as Mac
  const iPadOS13 = navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
  return iOSDevice || iPadOS13;
};

const isSafariBrowser = () => {
  const ua = navigator.userAgent || "";
  return /Safari/.test(ua) && !/(CriOS|FxiOS|EdgiOS|OPT|OPiOS)/.test(ua);
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    // iOS Safari legacy
    window.navigator.standalone === true
  );
};

const wasRecentlyDismissed = () => {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_WINDOW_MS;
  } catch {
    return false;
  }
};

export default function PWAInstall() {
  const { t } = useT();
  const [prompt, setPrompt] = useState(null);   // Chrome/Edge only
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("");         // "native" | "ios"

  // Register the service worker once — separate from the install banner logic.
  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onLoad = () => {
      navigator.serviceWorker
        .register("/sw.js")
        .then((reg) => {
          // If a new SW is waiting, tell it to skipWaiting so the fresh bundle
          // is served on the next navigation — no manual refresh required.
          if (reg.waiting) reg.waiting.postMessage({ type: "SKIP_WAITING" });
          reg.addEventListener?.("updatefound", () => {
            const sw = reg.installing;
            if (!sw) return;
            sw.addEventListener("statechange", () => {
              if (sw.state === "installed" && navigator.serviceWorker.controller) {
                sw.postMessage({ type: "SKIP_WAITING" });
              }
            });
          });
        })
        .catch(() => {});
    };
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad, { once: true });
    return () => window.removeEventListener("load", onLoad);
  }, []);

  // Install-prompt detection (Chrome/Edge/Android + iOS fallback)
  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    let showTimer;
    const nativeHandler = (e) => {
      e.preventDefault();
      setPrompt(e);
      setMode("native");
      showTimer = setTimeout(() => setVisible(true), 8000);
    };
    window.addEventListener("beforeinstallprompt", nativeHandler);

    // iOS fallback (event never fires there)
    if (isIOS() && isSafariBrowser()) {
      setMode("ios");
      showTimer = setTimeout(() => setVisible(true), 8000);
    }

    // Hide instantly once installed
    const installedHandler = () => {
      setVisible(false);
      setPrompt(null);
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    };
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", nativeHandler);
      window.removeEventListener("appinstalled", installedHandler);
      if (showTimer) clearTimeout(showTimer);
    };
  }, []);

  const dismiss = useCallback(() => {
    setVisible(false);
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
  }, []);

  const install = useCallback(async () => {
    if (!prompt) return;
    try {
      prompt.prompt();
      const choice = await prompt.userChoice;
      if (choice?.outcome === "dismissed") {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
      }
    } catch { /* ignore */ }
    setVisible(false);
    setPrompt(null);
  }, [prompt]);

  if (!visible) return null;

  // ─── Native install banner (Chrome / Edge / Android) ───
  if (mode === "native") {
    return (
      <div
        className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 sm:max-w-sm bg-white border-2 border-zinc-900 p-4 shadow-[6px_6px_0_0_#C8102E] animate-in slide-in-from-bottom-4"
        data-testid="pwa-install-banner"
        role="dialog"
        aria-label={t("pwa.install_title")}
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 shrink-0 grid place-items-center bg-[#C8102E]">
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-sm tracking-tight" data-testid="pwa-install-title">
              {t("pwa.install_title")}
            </div>
            <p className="text-xs text-zinc-600 mt-1">{t("pwa.install_sub")}</p>
          </div>
          <button
            onClick={dismiss}
            className="p-1 text-zinc-500 hover:text-zinc-900 -mr-1 -mt-1"
            data-testid="pwa-install-dismiss"
            aria-label={t("pwa.dismiss")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <button
          onClick={install}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#C8102E] hover:bg-[#9A0C24] text-white text-sm font-semibold py-2.5"
          data-testid="pwa-install-confirm"
        >
          <Download className="w-4 h-4" /> {t("pwa.install_cta")}
        </button>
      </div>
    );
  }

  // ─── iOS Safari banner (custom instructions) ───
  if (mode === "ios") {
    return (
      <div
        className="fixed bottom-4 right-4 left-4 sm:left-auto z-50 sm:max-w-sm bg-white border-2 border-zinc-900 p-4 shadow-[6px_6px_0_0_#C8102E] animate-in slide-in-from-bottom-4"
        data-testid="pwa-install-banner-ios"
        role="dialog"
        aria-label={t("pwa.install_title")}
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 shrink-0 grid place-items-center bg-[#C8102E]">
            <Smartphone className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-black text-sm tracking-tight">{t("pwa.install_title")}</div>
            <p className="text-xs text-zinc-600 mt-1">{t("pwa.ios_sub")}</p>
          </div>
          <button
            onClick={dismiss}
            className="p-1 text-zinc-500 hover:text-zinc-900 -mr-1 -mt-1"
            data-testid="pwa-install-dismiss"
            aria-label={t("pwa.dismiss")}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <ol className="mt-3 space-y-2 text-xs text-zinc-800">
          <li className="flex items-center gap-2">
            <span className="grid place-items-center w-5 h-5 bg-zinc-900 text-white text-[10px] font-black shrink-0">1</span>
            <span>{t("pwa.ios_step_1")}</span>
            <Share className="w-4 h-4 text-blue-600" />
          </li>
          <li className="flex items-center gap-2">
            <span className="grid place-items-center w-5 h-5 bg-zinc-900 text-white text-[10px] font-black shrink-0">2</span>
            <span>{t("pwa.ios_step_2")}</span>
            <Plus className="w-4 h-4 text-zinc-700" />
          </li>
          <li className="flex items-center gap-2">
            <span className="grid place-items-center w-5 h-5 bg-zinc-900 text-white text-[10px] font-black shrink-0">3</span>
            <span>{t("pwa.ios_step_3")}</span>
          </li>
        </ol>
      </div>
    );
  }

  return null;
}
